import { twMerge, twJoin } from '@troon/tailwind-preset/merge';
import { Show, mergeProps, splitProps, useContext } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { useFocusable } from '../providers/focusable';
import { ActivityIndicator } from '../components/activity-indicator';
import { FormContext } from './form';
import type { JSX, ComponentProps } from 'solid-js';
import type { Link } from '../components/link';

type AllProps = {
	action?: 'danger';
	appearance?:
		| 'primary'
		| 'secondary'
		| 'secondary-current'
		| 'tertiary'
		| 'tertiary-current'
		| 'transparent'
		| 'transparent-current';
	disabled?: boolean;
	size?: 'sm' | 'lg';
	pending?: boolean;
};
type LinkProps<T extends typeof Link = typeof Link> = {
	as: T;
} & AllProps &
	ComponentProps<T>;

type ButtonProps = {
	as?: 'button' | undefined;
} & AllProps &
	JSX.ButtonHTMLAttributes<HTMLButtonElement>;

type SummaryProps = {
	as: 'summary';
} & AllProps &
	JSX.HTMLAttributes<HTMLElement>;

type Props = LinkProps | ButtonProps | SummaryProps;

export function Button(inputProps: Props) {
	const { data } = useContext(FormContext);
	const focusableProps = useFocusable<Props>();
	const allProps = mergeProps({ as: 'button', appearance: 'primary' as const }, focusableProps, inputProps);
	const [props, rest] = splitProps(allProps, ['as', 'children', 'action', 'appearance', 'class', 'size', 'pending']);

	return (
		<Dynamic
			{...rest}
			component={props.as}
			disabled={
				props.as === 'button' &&
				// @ts-expect-error TS is choking on the union
				rest.type === 'submit'
					? data?.pending || props.pending || rest.disabled
					: rest.disabled
			}
			aria-disabled={props.as !== 'button' ? data?.pending || props.pending || rest.disabled : rest.disabled}
			formnovalidate={props.as === 'button' || undefined}
			class={twMerge(
				twJoin(
					'flex grow cursor-pointer flex-wrap items-center justify-center gap-x-2 rounded-lg border text-center font-semibold text-wrap whitespace-nowrap uppercase transition-all duration-200 outline-none focus-visible:ring active:scale-95 aria-disabled:cursor-not-allowed aria-disabled:active:scale-100',
					props.size === 'sm' && 'rounded-lg px-3 py-2.5 text-sm',
					(!props.size || props.size === 'lg') && 'rounded-lg px-4 py-3 text-base',

					props.appearance === 'primary' && !props.action
						? 'border-brand bg-brand text-white hover:border-brand-600 hover:bg-brand-600 focus-visible:ring-brand-100 active:border-brand-700 active:bg-brand-700 aria-disabled:border-neutral-300 aria-disabled:bg-neutral-300 aria-disabled:text-neutral-700 aria-disabled:hover:border-neutral-300 aria-disabled:hover:bg-neutral-300 aria-disabled:active:border-neutral-300 aria-disabled:active:bg-neutral-300'
						: '',
					props.appearance === 'primary' && props.action === 'danger'
						? 'border-red-500 bg-red-500 text-white hover:border-red-600 hover:bg-red-600 focus-visible:ring-red-100 active:border-red-700 active:bg-red-700 aria-disabled:border-red-200 aria-disabled:bg-red-200 aria-disabled:text-red-600 aria-disabled:hover:border-red-200 aria-disabled:hover:bg-red-200 aria-disabled:active:border-red-200 aria-disabled:active:bg-red-200'
						: '',

					props.appearance === 'secondary' && !props.action
						? 'border-brand bg-white text-brand hover:bg-brand-100 focus-visible:ring-brand-100 active:bg-brand-100 aria-disabled:border-neutral-300 aria-disabled:text-neutral-700 aria-disabled:hover:bg-white aria-disabled:active:bg-white'
						: '',
					props.appearance === 'secondary' && props.action === 'danger'
						? 'border-red-500 bg-white text-red-500 hover:bg-red-100 focus-visible:ring-red-100 active:bg-red-100 aria-disabled:border-red-300 aria-disabled:text-red-300 aria-disabled:hover:bg-white aria-disabled:active:bg-white'
						: '',
					props.appearance === 'secondary-current' && !props.action
						? 'border-current bg-transparent text-current hover:bg-current/10 focus-visible:ring-current/30 active:bg-current/30 aria-disabled:border-neutral-700 aria-disabled:text-neutral-700 aria-disabled:hover:bg-transparent aria-disabled:active:bg-transparent'
						: '',

					props.appearance === 'tertiary' && !props.action
						? 'border-neutral bg-white text-brand hover:bg-brand-100 focus-visible:ring-brand-100 active:bg-brand-100 aria-disabled:border-neutral-300 aria-disabled:text-neutral-700 aria-disabled:hover:bg-white aria-disabled:active:bg-white'
						: '',
					props.appearance === 'tertiary' && props.action === 'danger'
						? 'border-neutral bg-white text-red-500 hover:bg-red-100 focus-visible:ring-red-100 active:bg-red-100 aria-disabled:border-red-300 aria-disabled:text-red-300 aria-disabled:hover:bg-white aria-disabled:active:bg-white'
						: '',
					props.appearance === 'tertiary-current' && !props.action
						? 'border-neutral bg-transparent text-current hover:bg-current/10 focus-visible:ring-current/30 active:bg-current/30 aria-disabled:border-neutral-700 aria-disabled:text-neutral-700 aria-disabled:hover:bg-transparent aria-disabled:active:bg-transparent'
						: '',

					props.appearance === 'transparent' && !props.action
						? 'border-transparent text-brand hover:bg-brand-100 focus-visible:ring-brand-100 active:bg-brand-200 aria-disabled:bg-transparent aria-disabled:text-neutral-700'
						: '',

					props.appearance === 'transparent' && props.action === 'danger'
						? 'border-transparent text-red-500 hover:bg-red-100 focus-visible:ring-red-100 active:bg-red-200 aria-disabled:bg-transparent aria-disabled:text-neutral-700'
						: '',

					props.appearance === 'transparent-current'
						? 'border-transparent text-current hover:bg-current/10 focus-visible:ring-current/30 active:bg-current/30 aria-disabled:bg-transparent aria-disabled:text-neutral-700'
						: '',
				),
				props.class,
			)}
		>
			<Show
				when={
					(data.pending &&
						props.as === 'button' &&
						// @ts-expect-error TS is choking on the union
						rest.type === 'submit') ||
					props.pending
				}
			>
				<ActivityIndicator aria-label="Loading" class="me-2 size-4" />
			</Show>
			{props.children}
		</Dynamic>
	);
}
